/**
 * Gammes d'entretien — liste à gauche, lecture de la gamme à droite (plan « Gammes d'entretien »,
 * étapes A1 puis A2.1).
 *
 * MISE EN PAGE (retour de Benjamin, 2026-10-08). Une page en deux colonnes, comme Le Parc : on
 * choisit une gamme dans la liste et on la lit tout de suite à côté, sans descendre en bas de page
 * ni remonter. Chaque colonne défile à la molette indépendamment de l'autre. L'en-tête de la
 * gamme — son nom, ses boutons et les messages du dernier geste — reste collé en haut de la
 * colonne de droite : un refus (« encore rattachée à 2 machines ») s'affiche là où l'on a cliqué,
 * jamais hors de l'écran. Un champ de recherche en tête de liste évite de la parcourir ligne à ligne.
 *
 * Qui détient le droit de gérer les gammes peut en créer une, modifier son libellé, ses modèles et ses
 * références, l'archiver et la réactiver, créer ses versions et composer leur contenu (paliers,
 * pièces, prix) dans l'éditeur. Tant qu'un brouillon est en cours de modification, la liste de
 * gauche est verrouillée : le travail non enregistré ne doit pas disparaître en changeant de gamme.
 * Le rattachement d'une machine se fait depuis sa fiche dans Le Parc, ou en lot depuis la gamme.
 *
 * Les boutons dépendent des droits lus dans le jeton, MAIS seule l'API décide : sans le droit, elle
 * refuse, et l'écran affiche son refus.
 *
 * La recherche (`?q=`) et la gamme sélectionnée (`?gamme=`) sont portées par l'URL, comme le Parc :
 * l'écran se copie, se partage, et survit à un rechargement.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ApiError,
  api,
  type GammeDetail,
  type GammeResume,
  type GammeVersion,
} from '../api/client';
import { useDroitsGammes } from '../auth/useDroits';
import { ActionsVersion } from '../composants/ActionsVersion';
import { ContenuGamme } from '../composants/ContenuGamme';
import { EditeurGamme } from '../composants/EditeurGamme';
import { ErreurFormulaire } from '../composants/Formulaire';
import { FormulaireGamme } from '../composants/FormulaireGamme';
import { FormulaireVersion } from '../composants/FormulaireVersion';
import { ListeGammes } from '../composants/ListeGammes';
import { MachinesCorrespondantes } from '../composants/MachinesCorrespondantes';
import { STATUT_VERSION, dateFr, listerReferences } from '../domaine/gammes';
import { libelleCritere, messageGammeCopiee } from '../domaine/gammes-identite';
import { filtrerGammes } from '../domaine/gammes-recherche';
import { bilanRattachement, type ResultatRattachement } from '../domaine/gammes-versions';

export function MaintenanceProfilesPage() {
  const [parametres, setParametres] = useSearchParams();
  const selectionnee = parametres.get('gamme');
  const droits = useDroitsGammes();
  const emplacement = useLocation();
  const naviguer = useNavigate();

  const [recherche, setRecherche] = useState(parametres.get('q') ?? '');
  const [gammes, setGammes] = useState<GammeResume[] | null>(null);
  const [detail, setDetail] = useState<GammeDetail | null>(null);
  const [versionId, setVersionId] = useState<string | null>(null);
  /**
   * La version à garder affichée quand la gamme est relue après un geste (publier, archiver, en
   * créer une…) : sans elle, la relecture retomberait sur la version en vigueur. Elle part de `?version=` de
   * l'adresse : c'est ce qui permet à un lien (le bilan de la mise à jour d'après un tarif) d'ouvrir un brouillon
   * précis. L'adresse la perd ensuite à la première action de l'utilisateur (`majUrl`), sans conséquence.
   */
  const [versionVoulue, setVersionVoulue] = useState<string | null>(parametres.get('version'));
  const [erreur, setErreur] = useState<string | null>(null);

  const [afficherArchivees, setAfficherArchivees] = useState(false);
  const [panneau, setPanneau] = useState<'creation' | 'edition' | 'copie' | null>(null);
  /** Première version d'une gamme qui n'en a aucune : un brouillon vide. */
  const [panneauPremiere, setPanneauPremiere] = useState(false);
  /** Le contenu du brouillon affiché est en cours d'édition dans l'éditeur. */
  const [edition, setEdition] = useState(false);
  /** La gamme dont on a déjà chargé le détail : pour ne vider la colonne que si on en change. */
  const derniereSelection = useRef<string | null>(null);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  /** Incrémenté pour relire le détail de la gamme affichée après une modification. */
  const [rechargement, setRechargement] = useState(0);

  const filtrees = useMemo(() => filtrerGammes(gammes ?? [], recherche), [gammes, recherche]);

  // Un message venu d'une autre page (la fiche d'une machine vient de créer une gamme) : on l'affiche
  // puis on l'efface de l'historique, pour qu'il ne revienne pas à un rechargement.
  useEffect(() => {
    const message = (emplacement.state as { message?: string } | null)?.message;
    if (!message) return;
    setSucces(message);
    naviguer(`${emplacement.pathname}${emplacement.search}`, { replace: true, state: null });
  }, [emplacement, naviguer]);

  /** Écrit la gamme choisie et la recherche dans l'URL, sans encombrer l'historique. */
  const majUrl = useCallback(
    (gamme: string | null, q: string) => {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q);
      if (gamme) p.set('gamme', gamme);
      setParametres(p, { replace: true });
    },
    [setParametres],
  );

  const chargerListe = useCallback(async () => {
    try {
      setGammes(await api.maintenanceProfiles(afficherArchivees));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement des gammes impossible.');
    }
  }, [afficherArchivees]);

  useEffect(() => {
    void chargerListe();
  }, [chargerListe]);

  // Rien n'est choisi : on montre la première gamme de la liste plutôt qu'une colonne vide.
  useEffect(() => {
    const premiere = filtrees[0];
    if (gammes !== null && !selectionnee && premiere) majUrl(premiere.id, recherche);
  }, [gammes, filtrees, selectionnee, recherche, majUrl]);

  useEffect(() => {
    // On ne vide la colonne de droite que si l'on CHANGE de gamme. Relire la même gamme après un geste
    // (enregistrer, publier…) garde l'écran en place : l'éditeur ouvert, le défilement et la saisie
    // en cours ne sautent pas.
    if (derniereSelection.current !== selectionnee) {
      derniereSelection.current = selectionnee;
      setDetail(null);
      setVersionId(null);
      setEdition(false);
    }
    if (!selectionnee) return;
    let annule = false;
    api
      .maintenanceProfile(selectionnee)
      .then((d) => {
        if (annule) return;
        setDetail(d);
        // La version à montrer d'abord : celle qu'un geste vient de toucher, sinon la publiée en
        // vigueur, sinon la plus récente.
        const voulue = versionVoulue ? d.versions.find((v) => v.id === versionVoulue) : undefined;
        const enVigueur = d.versions.find(
          (v) => v.statut === 'PUBLIEE' && d.enVigueur?.versionNumber === v.versionNumber,
        );
        const choisie = voulue ?? enVigueur ?? d.versions[0];
        setVersionId(choisie?.id ?? null);
        // L'éditeur ne s'ouvre que sur un brouillon : si la version n'en est plus un (publiée ou
        // abandonnée par quelqu'un d'autre), on le ferme.
        if (choisie?.statut !== 'BROUILLON') setEdition(false);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement de la gamme impossible.');
      });
    return () => {
      annule = true;
    };
    // `versionVoulue` n'est lue qu'à la relecture déclenchée par `rechargement` ou `selectionnee` :
    // la changer ne doit pas relancer la lecture à elle seule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionnee, rechargement]);

  const selectionner = (id: string) => {
    setSucces(null);
    setErreurAction(null);
    setVersionVoulue(null);
    majUrl(id, recherche);
  };

  /**
   * La liste suit la saisie, et la gamme affichée la suit aussi : si celle qui est lue ne répond
   * plus à la recherche, on lit la première qui y répond (sans quoi la colonne de droite resterait
   * figée sur une gamme que la liste ne montre plus).
   */
  const changerRecherche = (saisie: string) => {
    setRecherche(saisie);
    const resultat = filtrerGammes(gammes ?? [], saisie);
    const premiere = resultat[0];
    if (selectionnee && resultat.some((g) => g.id === selectionnee)) {
      majUrl(selectionnee, saisie);
    } else if (premiere) {
      setSucces(null);
      setErreurAction(null);
      majUrl(premiere.id, saisie);
    } else {
      majUrl(selectionnee, saisie);
    }
  };

  const rafraichir = async () => {
    setRechargement((n) => n + 1);
    await chargerListe();
  };

  /**
   * Une action sur une gamme : efface les messages, l'exécute, annonce le résultat puis relit.
   * Le message d'erreur est posé APRÈS la relecture, qui ne l'efface pas.
   */
  const agir = async (action: () => Promise<unknown>, message: string, versionAGarder?: string) => {
    setErreurAction(null);
    setSucces(null);
    setEnCours(true);
    try {
      await action();
      setSucces(message);
      setVersionVoulue(versionAGarder ?? null);
      await rafraichir();
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Action impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const apresCreation = (g: GammeResume) => {
    setPanneau(null);
    setErreurAction(null);
    setSucces(
      `Gamme « ${g.label} » créée. Elle n’a pas encore de pièces : créez sa première version, puis composez-la avec « Modifier le contenu ».`,
    );
    // La nouvelle gamme doit se voir : une recherche en cours pourrait la masquer.
    setRecherche('');
    majUrl(g.id, '');
    void rafraichir();
  };

  const apresCopie = (g: GammeResume, origine: string) => {
    setPanneau(null);
    setErreurAction(null);
    setSucces(messageGammeCopiee(g.label, origine));
    setRecherche('');
    setVersionVoulue(null);
    majUrl(g.id, '');
    void rafraichir();
  };

  const apresModification = () => {
    setPanneau(null);
    setErreurAction(null);
    setSucces('Gamme modifiée.');
    void rafraichir();
  };

  const apresVersionCreee = (v: GammeVersion, depuis: GammeVersion) => {
    setErreurAction(null);
    setSucces(
      `Brouillon de la version ${v.versionNumber} créé, copié de la version ${depuis.versionNumber}. ` +
        'Il ne s’applique à aucune machine tant qu’il n’est pas publié ; modifiez son contenu avec « Modifier le contenu ».',
    );
    setVersionVoulue(v.id);
    void rafraichir();
  };

  const apresPremiereVersion = (v: GammeVersion) => {
    setPanneauPremiere(false);
    setErreurAction(null);
    setSucces(
      `Première version créée : un brouillon vide. Composez-le avec « Modifier le contenu » ; ` +
        'il ne s’applique à aucune machine tant qu’il n’est pas publié.',
    );
    setVersionVoulue(v.id);
    void rafraichir();
  };

  const apresContenuEnregistre = (v: GammeVersion, compte: { paliers: number; lignes: number }) => {
    setErreurAction(null);
    setSucces(
      `Contenu enregistré : ${compte.paliers} palier${compte.paliers > 1 ? 's' : ''}, ` +
        `${compte.lignes} ligne${compte.lignes > 1 ? 's' : ''}. Le brouillon n’est pas publié.`,
    );
    setVersionVoulue(v.id);
    void rafraichir();
  };

  const apresVersionModifiee = (v: GammeVersion) => {
    setErreurAction(null);
    setSucces(`Informations de la version ${v.versionNumber} enregistrées.`);
    setVersionVoulue(v.id);
    void rafraichir();
  };

  /** Bilan d'un rattachement en lot : le succès, puis les refus avec leur raison, APRÈS la relecture. */
  const apresRattachement = async (resultats: ResultatRattachement[]) => {
    const bilan = bilanRattachement(resultats);
    setSucces(bilan.succes);
    setErreurAction(null);
    await rafraichir();
    if (bilan.refus) setErreurAction(new Error(bilan.refus));
  };

  const archiver = (g: GammeResume) => {
    if (g.machinesRattachees > 0) {
      setSucces(null);
      setErreurAction(
        new Error(
          `« ${g.label} » est encore rattachée à ${g.machinesRattachees} machine${g.machinesRattachees > 1 ? 's' : ''} : ` +
            'détachez-les d’abord, depuis leur fiche dans Le Parc.',
        ),
      );
      return;
    }
    const confirme = window.confirm(
      `Archiver la gamme « ${g.label} » ?\n\n` +
        'Elle ne sera plus proposée aux machines. Rien n’est supprimé : ses versions restent lisibles, ' +
        'et vous pourrez la réactiver.',
    );
    if (!confirme) return;
    void agir(() => api.archiveMaintenanceProfile(g.id), 'Gamme archivée.');
  };

  const version: GammeVersion | null = detail?.versions.find((v) => v.id === versionId) ?? null;

  const messages = (
    <>
      <ErreurFormulaire
        message={erreurAction?.message ?? null}
        details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
      />
      {succes && <div className="message succes">{succes}</div>}
    </>
  );

  return (
    <div className="gammes-page">
      <div className="entete-page">
        <div>
          <h1>Gammes d’entretien</h1>
          <p>Pièces et opérations à prévoir à chaque palier, par modèle de machine.</p>
        </div>
        {droits.peutGerer && (
          <button disabled={edition} onClick={() => setPanneau('creation')}>
            Nouvelle gamme
          </button>
        )}
      </div>

      {!droits.peutGerer && (
        <div className="message info">
          Vous consultez les gammes en lecture seule : en créer ou en modifier demande le droit de gérer
          les gammes. Le rattachement d’une machine se fait depuis sa fiche, dans{' '}
          <Link to="/parc">Le Parc</Link>.
        </div>
      )}

      <div className="gammes-mise-en-page">
        <ListeGammes
          gammes={gammes}
          filtrees={filtrees}
          recherche={recherche}
          onRecherche={changerRecherche}
          selectionnee={selectionnee}
          onSelection={selectionner}
          afficherArchivees={afficherArchivees}
          onArchivees={setAfficherArchivees}
          erreur={erreur}
          verrouillee={edition}
        />

        <div className="gammes-fiche">
          {!detail && (erreurAction || succes) && (
            <div className="gammes-fiche-entete">{messages}</div>
          )}

          {!selectionnee && (
            <div className="carte">
              <div className="vide">
                Choisissez une gamme dans la liste pour la lire ici. Une version publiée ne change plus
                jamais : toute correction crée une nouvelle version.
              </div>
            </div>
          )}

          {selectionnee && !detail && !erreur && <div className="vide">Chargement de la gamme…</div>}

          {detail && (
            <>
              <div className="gammes-fiche-entete">
                <div className="gammes-fiche-titre">
                  <h2>
                    {detail.label} <span className="aide">({detail.code})</span>
                    {detail.archivedAt && (
                      <>
                        {' '}
                        <span className="etat revoque">Archivée</span>
                      </>
                    )}
                  </h2>
                  {version && (
                    <div className="saisie-ligne">
                      <Link
                        className="bouton-lien secondaire-lien"
                        to={`/gammes/imprimer?gamme=${detail.id}&version=${version.id}`}
                        title={`Imprimer ou exporter la version ${version.versionNumber} de cette gamme`}
                      >
                        Imprimer / Exporter
                      </Link>
                    </div>
                  )}
                  {droits.peutGerer && (
                    <div className="saisie-ligne">
                      {detail.archivedAt ? (
                        <button
                          className="secondaire"
                          disabled={enCours}
                          onClick={() =>
                            void agir(() => api.unarchiveMaintenanceProfile(detail.id), 'Gamme réactivée.')
                          }
                        >
                          Réactiver
                        </button>
                      ) : (
                        <>
                          <button
                            className="secondaire"
                            disabled={enCours || edition}
                            onClick={() => setPanneau('edition')}
                          >
                            Modifier
                          </button>
                          {version && (
                            <button
                              className="secondaire"
                              disabled={enCours || edition}
                              title={`Crée une nouvelle gamme qui reprend le contenu et les prix de la version ${version.versionNumber}`}
                              onClick={() => setPanneau('copie')}
                            >
                              Nouvelle gamme à partir de celle-ci
                            </button>
                          )}
                          <button
                            className="secondaire danger-discret"
                            disabled={enCours || edition}
                            onClick={() => archiver(detail)}
                          >
                            Archiver
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {messages}
              </div>

              <div className="carte">
                <div className="carte-corps">
                  <p className="aide">
                    Marque : <strong>{detail.brandName}</strong> · critères de suggestion :{' '}
                    {detail.criteres.length === 0
                      ? 'aucun (rattachement manuel uniquement)'
                      : detail.criteres.map(libelleCritere).join(', ')}
                    {detail.referencesConstructeur.length > 0 && (
                      <>
                        {' '}
                        · établie pour la référence constructeur{' '}
                        <strong>{listerReferences(detail.referencesConstructeur)}</strong>
                      </>
                    )}
                  </p>

                  {detail.versions.length === 0 ? (
                    <div className="vide">
                      Cette gamme n’a encore aucune version.
                      {droits.peutGerer && !detail.archivedAt && (
                        <p>
                          <button disabled={enCours} onClick={() => setPanneauPremiere(true)}>
                            Créer la première version
                          </button>
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="chips-secondaires" role="tablist" aria-label="Versions de la gamme">
                      {detail.versions.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          role="tab"
                          aria-selected={v.id === versionId}
                          className={`chip${v.id === versionId ? ' actif' : ''}`}
                          disabled={edition}
                          onClick={() => setVersionId(v.id)}
                        >
                          v{v.versionNumber} · {STATUT_VERSION[v.statut].texte}
                        </button>
                      ))}
                    </div>
                  )}

                  {version && (
                    <>
                      {!edition && (
                        <ActionsVersion
                          detail={detail}
                          version={version}
                          droits={droits}
                          enCours={enCours}
                          agir={agir}
                          onCree={apresVersionCreee}
                          onModifiee={apresVersionModifiee}
                          onConflit={(versionAGarder) => {
                            setErreurAction(null);
                            setSucces('Version relue : elle a été modifiée par quelqu’un d’autre depuis votre ouverture.');
                            setVersionVoulue(versionAGarder);
                            void rafraichir();
                          }}
                          onEditerContenu={() => setEdition(true)}
                        />
                      )}
                      <p className="aide">
                        <span className={`etat ${STATUT_VERSION[version.statut].classe}`}>
                          {STATUT_VERSION[version.statut].texte}
                        </span>{' '}
                        Effet au {dateFr(version.validFrom)}
                        {version.publishedAt && ` · publiée le ${dateFr(version.publishedAt)}`}
                        {' — '}
                        {version.motif}
                        <br />
                        Source : {version.source}
                      </p>
                      {edition && version.statut === 'BROUILLON' ? (
                        <EditeurGamme
                          key={`${version.id}-${version.revision}`}
                          gamme={detail}
                          version={version}
                          droits={droits}
                          onEnregistre={apresContenuEnregistre}
                          onFermer={() => setEdition(false)}
                          onConflit={() => {
                            setErreurAction(null);
                            setSucces('Version relue : elle a été modifiée par quelqu’un d’autre depuis votre ouverture.');
                            setVersionVoulue(version.id);
                            void rafraichir();
                          }}
                        />
                      ) : (
                        <ContenuGamme version={version} />
                      )}
                    </>
                  )}
                </div>
              </div>

              <MachinesCorrespondantes
                detail={detail}
                peutGerer={droits.peutGerer}
                enCours={enCours}
                onRattachees={(resultats) => void apresRattachement(resultats)}
              />
            </>
          )}
        </div>
      </div>

      {panneau === 'creation' && (
        <FormulaireGamme onFermer={() => setPanneau(null)} onEnregistree={apresCreation} />
      )}
      {panneau === 'edition' && detail && (
        <FormulaireGamme
          gamme={detail}
          onFermer={() => setPanneau(null)}
          onEnregistree={apresModification}
        />
      )}
      {panneau === 'copie' && detail && version && (
        <FormulaireGamme
          copie={{ depuis: detail, version }}
          onFermer={() => setPanneau(null)}
          onEnregistree={(g) => apresCopie(g, detail.label)}
        />
      )}
      {panneauPremiere && detail && (
        <FormulaireVersion
          mode="premiere"
          gamme={detail}
          version={null}
          onFermer={() => setPanneauPremiere(false)}
          onEnregistree={apresPremiereVersion}
          onConflit={() => setPanneauPremiere(false)}
        />
      )}
    </div>
  );
}
